import React, {FunctionComponent, useEffect} from "react";
import NiceModal, {NiceModalHocProps, useModal} from "@ebay/nice-modal-react";
import {Container, Text, Dialog} from "@bounce/shared/ui";
import {Controller, useForm} from "react-hook-form";
import {EMAIL_REGEX} from "@bounce/shared/constants";
import {useAmplitude} from "../../contexts/AmplitudeContext";
import {Pages} from "../../bi-events/pages";
import {TextField} from "@mui/material";
import {useAuthContext} from "../../contexts/AuthContext";
import {ContactUsRequest} from "../../declarations";
import {sendContactUs} from "../../api/requests/partner-level";

interface ContactUsDialogProps extends NiceModalHocProps {}

type FormValues = ContactUsRequest;

const ContactUsDialog: FunctionComponent<ContactUsDialogProps> = NiceModal.create(() => {
  const {pageViewEvent, pageClickEvent} = useAmplitude();
  const modal = useModal();

  const {email} = useAuthContext();
  const {control, handleSubmit} = useForm<FormValues>({defaultValues: {email: email || ""}});

  const onSubmit = handleSubmit(async (data): Promise<void> => {
    try {
      sendContactUs({contactUsRequest: data});
      modal.resolve(true);
      modal.hide();
    } catch (e) {
      modal.resolve(false);
    } finally {
      pageClickEvent({
        page: Pages.CONTACT_US,
        cta: "send",
        data: {text: data.message, subject: data.subject, sender_name: data.sender_name, email: data.email},
      });
    }
  });

  useEffect(() => {
    pageViewEvent({page: Pages.CONTACT_US});
  }, []);

  return (
    <Dialog title={"How can we help?"} buttonText={"Send"} onClick={onSubmit} autoClose={false}>
      <Text>Send us a message below, and we will contact you shortly.</Text>
      <form style={{width: "100%"}}>
        <Container sx={{gap: "8px", alignItems: "start", height: "320px"}}>
          <Controller
            name="email"
            rules={{
              required: "Required field",
              pattern: {value: EMAIL_REGEX, message: "Email invalid"},
            }}
            control={control}
            defaultValue=""
            render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
              return (
                <TextField
                  {...field}
                  inputRef={ref}
                  fullWidth
                  placeholder={"Email"}
                  type="email"
                  error={invalid}
                  helperText={error?.message}
                  inputProps={{
                    maxLength: 50,
                  }}
                  InputProps={{
                    style: {borderRadius: "8px"},
                  }}
                />
              );
            }}
          />
          <Controller
            name="sender_name"
            rules={{
              required: "Required field",
            }}
            control={control}
            defaultValue=""
            render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
              return (
                <TextField
                  {...field}
                  inputRef={ref}
                  fullWidth
                  placeholder={"Sender name"}
                  error={invalid}
                  helperText={error?.message}
                  inputProps={{
                    maxLength: 50,
                  }}
                  InputProps={{
                    style: {borderRadius: "8px"},
                  }}
                />
              );
            }}
          />
          <Controller
            name="subject"
            rules={{
              required: "Required field",
            }}
            control={control}
            defaultValue=""
            render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
              return (
                <TextField
                  {...field}
                  inputRef={ref}
                  fullWidth
                  placeholder={"Subject"}
                  error={invalid}
                  helperText={error?.message}
                  inputProps={{
                    maxLength: 50,
                  }}
                  InputProps={{
                    style: {borderRadius: "8px"},
                  }}
                />
              );
            }}
          />
          <Controller
            name="message"
            rules={{
              required: "Required field",
            }}
            control={control}
            defaultValue={""}
            render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
              return (
                <TextField
                  {...field}
                  placeholder={"Type Your Message Here"}
                  inputRef={ref}
                  variant={"outlined"}
                  helperText={error?.message}
                  margin="normal"
                  fullWidth
                  type="text"
                  multiline
                  rows={4}
                  autoComplete="off"
                  error={invalid}
                  InputLabelProps={{required: false}}
                  inputProps={{
                    maxLength: 1024,
                  }}
                  InputProps={{
                    style: {borderRadius: "8px"},
                  }}
                  sx={{margin: "0px"}}
                />
              );
            }}
          />
        </Container>
      </form>
    </Dialog>
  );
});

export default ContactUsDialog;
